import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@omnidars/shared/stores/auth'
import { useEntitlements } from '@omnidars/shared'

const routes = [
  // Top-level auth routes (no layout wrapper - matches tenant-admin-portal pattern)
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/sso/callback',
    name: 'SSOCallback',
    component: () => import('@omnidars/shared').then(m => m.SSOCallback),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
      },
      // Bots
      {
        path: 'bots',
        name: 'BotsList',
        component: () => import('@/views/bots/BotsListView.vue'),
      },
      // Flows (Independent)
      {
        path: 'flows',
        name: 'FlowsList',
        component: () => import('@/views/flows/FlowsListView.vue'),
      },
      {
        path: 'flows/:flowId/builder',
        name: 'FlowBuilder',
        component: () => import('@/views/flows/FlowBuilderView.vue'),
      },
      // v1.4.0 — drill-in route for group editing inside an independent flow
      {
        path: 'flows/:flowId/builder/group/:groupId',
        name: 'FlowBuilderGroup',
        component: () => import('@/views/flows/FlowBuilderView.vue'),
      },
      // v1.5.1 → consolidated: Subflows merged into the Flows page with
      // a Type filter pill (All / Flows / Subflows). /subflows and the
      // legacy /library URLs redirect to /flows?kind=subflow for one
      // release so any in-flight bookmarks still land on the right view.
      // LibraryListView.vue is removed in this release.
      {
        path: 'subflows',
        redirect: { path: '/flows', query: { kind: 'subflow' } },
      },
      {
        path: 'library',
        redirect: { path: '/flows', query: { kind: 'subflow' } },
      },
      {
        path: 'bots/create',
        name: 'BotCreate',
        component: () => import('@/views/bots/BotCreateView.vue'),
      },
      {
        path: 'bots/:botId',
        name: 'BotDetail',
        component: () => import('@/views/bots/BotDetailView.vue'),
      },
      {
        path: 'bots/:botId/edit',
        name: 'BotEdit',
        component: () => import('@/views/bots/BotEditView.vue'),
      },
      {
        path: 'bots/:botId/builder',
        name: 'BotBuilder',
        component: () => import('@/views/bots/builder/BotBuilderView.vue'),
      },
      {
        path: 'bots/:botId/builder/:flowId',
        name: 'BotFlowEditor',
        component: () => import('@/views/bots/builder/BotBuilderView.vue'),
      },
      // v1.4.0 — drill-in route for group editing inside a bot's flow
      {
        path: 'bots/:botId/builder/:flowId/group/:groupId',
        name: 'BotFlowEditorGroup',
        component: () => import('@/views/bots/builder/BotBuilderView.vue'),
      },
      {
        path: 'bots/:botId/test',
        name: 'BotTest',
        component: () => import('@/views/bots/BotTestView.vue'),
      },
      // Agent Hubs
      {
        path: 'hubs',
        name: 'AgentHubsList',
        component: () => import('@/views/hubs/AgentHubsListView.vue'),
        meta: { requiredFeatures: ['bots.multi_bot'] },
      },
      {
        path: 'hubs/create',
        name: 'AgentHubCreate',
        component: () => import('@/views/hubs/AgentHubEditorView.vue'),
        meta: { requiredFeatures: ['bots.multi_bot'] },
      },
      {
        path: 'hubs/:hubId/edit',
        name: 'AgentHubEdit',
        component: () => import('@/views/hubs/AgentHubEditorView.vue'),
        meta: { requiredFeatures: ['bots.multi_bot'] },
      },
      // Conversations
      {
        path: 'conversations',
        name: 'ConversationsList',
        component: () => import('@/views/conversations/ConversationsListView.vue'),
      },
      {
        path: 'conversations/:id',
        name: 'ConversationDetail',
        component: () => import('@/views/conversations/ConversationDetailView.vue'),
      },
      // Contacts
      {
        path: 'contacts',
        name: 'ContactsList',
        component: () => import('@/views/contacts/ContactsListView.vue'),
      },
      {
        path: 'contacts/create',
        name: 'ContactCreate',
        component: () => import('@/views/contacts/ContactCreateView.vue'),
      },
      {
        path: 'contacts/:contactId',
        name: 'ContactDetail',
        component: () => import('@/views/contacts/ContactDetailView.vue'),
      },
      // Segments
      {
        path: 'segments',
        name: 'SegmentsList',
        component: () => import('@/views/segments/SegmentsListView.vue'),
      },
      {
        path: 'segments/create',
        name: 'SegmentCreate',
        component: () => import('@/views/segments/SegmentBuilderView.vue'),
      },
      {
        path: 'segments/:segmentId',
        name: 'SegmentEdit',
        component: () => import('@/views/segments/SegmentBuilderView.vue'),
      },
      // KB Collections (RAG)
      {
        path: 'kb-collections',
        name: 'KBCollectionsList',
        component: () => import('@/views/knowledge-bases/KnowledgeBasesListView.vue'),
        meta: { requiredFeatures: ['knowledge.rag_knowledge_base'] },
      },
      {
        path: 'kb-collections/create',
        name: 'KBCollectionCreate',
        component: () => import('@/views/knowledge-bases/KnowledgeBaseCreateView.vue'),
        meta: { requiredFeatures: ['knowledge.rag_knowledge_base'] },
      },
      {
        path: 'kb-collections/:id',
        name: 'KBCollectionDetail',
        component: () => import('@/views/knowledge-bases/KnowledgeBaseDetailView.vue'),
        meta: { requiredFeatures: ['knowledge.rag_knowledge_base'] },
      },
      // AI Prompts
      {
        path: 'ai-prompts',
        name: 'AIPromptsList',
        component: () => import('@/views/ai-prompts/AIPromptsListView.vue'),
        meta: { requiredFeatures: ['knowledge.llm_conversations'] },
      },
      // Indexing Jobs
      {
        path: 'indexing-jobs',
        name: 'IndexingJobs',
        component: () => import('@/views/knowledge-bases/IndexingJobsView.vue'),
        meta: { requiredFeatures: ['knowledge.rag_knowledge_base'] },
      },
      {
        path: 'ai-prompts/create',
        name: 'AIPromptCreate',
        component: () => import('@/views/ai-prompts/AIPromptCreateView.vue'),
        meta: { requiredFeatures: ['knowledge.llm_conversations'] },
      },
      {
        path: 'ai-prompts/:id',
        name: 'AIPromptDetail',
        component: () => import('@/views/ai-prompts/AIPromptDetailView.vue'),
        meta: { requiredFeatures: ['knowledge.llm_conversations'] },
      },
      // Channels
      {
        path: 'channels',
        name: 'ChannelsList',
        component: () => import('@/views/channels/ChannelsView.vue'),
        meta: { requiredFeatures: ['channels.web_chat', 'channels.sms', 'channels.whatsapp', 'channels.facebook_messenger', 'channels.slack', 'channels.teams', 'channels.voice'] },
      },
      {
        path: 'channels/audit',
        name: 'ChannelAuditLogs',
        component: () => import('@/views/channels/ChannelAuditLogView.vue'),
        meta: { requiredFeatures: ['channels.web_chat', 'channels.sms', 'channels.whatsapp', 'channels.facebook_messenger', 'channels.slack', 'channels.teams', 'channels.voice'] },
      },
      // Audit Logs
      {
        path: 'audit-logs',
        name: 'AuditLogs',
        component: () => import('@/views/audit/AuditLogsView.vue'),
      },
      // Tools
      {
        path: 'tools',
        name: 'ToolsList',
        component: () => import('@/views/tools/ToolsListView.vue'),
      },
      {
        path: 'tools/templates',
        name: 'ToolTemplates',
        component: () => import('@/views/tools/ToolTemplatesView.vue'),
      },
      {
        path: 'tools/new',
        name: 'ToolCreate',
        component: () => import('@/views/tools/ToolEditorView.vue'),
      },
      {
        path: 'tools/:toolId/edit',
        name: 'ToolEdit',
        component: () => import('@/views/tools/ToolEditorView.vue'),
      },
      // Integrations (Read-only view)
      {
        path: 'integrations',
        name: 'IntegrationsList',
        component: () => import('@/views/integrations/IntegrationsListView.vue'),
        meta: { requiredFeatures: ['integrations.ccaas_genesys', 'integrations.ccaas_8x8', 'integrations.ccaas_amazon_connect', 'integrations.ccaas_google', 'integrations.dialogflow', 'integrations.custom_webhooks', 'integrations.api_access'] },
      },
      // MCP Servers
      {
        path: 'integrations/mcp-servers',
        name: 'MCPServers',
        component: () => import('@/views/integrations/MCPServersView.vue'),
        meta: { requiredFeatures: ['knowledge.mcp_servers'] },
      },
      // Billing
      {
        path: 'billing',
        name: 'Billing',
        component: () => import('@/views/billing/BillingView.vue'),
      },
      // Reports
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('@/views/reports/ReportsView.vue'),
      },
      {
        path: 'reports/usage',
        name: 'UsageReport',
        component: () => import('@/views/reports/UsageReportView.vue'),
      },
      {
        path: 'reports/custom/create',
        name: 'CustomReportCreate',
        component: () => import('@/views/reports/CustomReportBuilderView.vue'),
        meta: { requiredFeatures: ['analytics.custom_reports'] },
      },
      {
        path: 'reports/custom/:id',
        name: 'CustomReportEdit',
        component: () => import('@/views/reports/CustomReportBuilderView.vue'),
        meta: { requiredFeatures: ['analytics.custom_reports'] },
      },
      // Performance Dashboard
      {
        path: 'reports/dashboard',
        name: 'PerformanceDashboard',
        component: () => import('@/views/reports/PerformanceDashboardView.vue'),
      },
      // Analytics
      {
        path: 'analytics/tools',
        name: 'ToolAnalytics',
        component: () => import('@/views/analytics/ToolAnalyticsDashboard.vue'),
      },
      {
        path: 'analytics/voice',
        name: 'VoiceAnalytics',
        component: () => import('@/views/voice/VoiceAnalyticsView.vue'),
        meta: { requiredFeatures: ['voice.voice_ai_stt'] },
      },
      {
        path: 'analytics/dlp',
        name: 'DlpAnalytics',
        component: () => import('@/views/analytics/DlpAnalyticsView.vue'),
        meta: { requiredFeatures: ['analytics.audit_logs'] },
      },
      {
        path: 'analytics/cost-usage',
        name: 'CostUsage',
        component: () => import('@/views/analytics/CostUsageView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      {
        path: 'analytics/conversations',
        name: 'ConversationInsights',
        component: () => import('@/views/analytics/ConversationInsightsView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      {
        path: 'analytics/kb-performance',
        name: 'KBPerformance',
        component: () => import('@/views/analytics/KBPerformanceView.vue'),
        meta: { requiredFeatures: ['knowledge.rag_knowledge_base'] },
      },
      {
        path: 'analytics/bot-feedback',
        name: 'BotFeedback',
        component: () => import('@/views/analytics/BotFeedbackView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      // Notifications
      {
        path: 'notifications/triggers',
        name: 'NotificationTriggers',
        component: () => import('@/views/notifications/NotificationTriggersView.vue'),
        meta: { requiredFeatures: ['outreach.notification_triggers'] },
      },
      {
        path: 'notifications/preferences',
        name: 'NotificationPreferences',
        component: () => import('@/views/notifications/NotificationPreferencesView.vue'),
      },
      {
        path: 'notifications/analytics',
        name: 'NotificationAnalytics',
        component: () => import('@/views/notifications/NotificationAnalyticsView.vue'),
        meta: { requiredFeatures: ['outreach.notification_analytics'] },
      },
      // Intents
      {
        path: 'intents',
        name: 'IntentsList',
        component: () => import('@/views/intents/IntentsListView.vue'),
        meta: { requiredFeatures: ['knowledge.intent_detection'] },
      },
      // Security
      {
        path: 'security',
        name: 'SecurityDashboard',
        component: () => import('@/views/security/SecurityDashboardView.vue'),
        meta: { requiredFeatures: ['security.ip_allowlist'] },
      },
      // Engagement Hub
      {
        path: 'engagement-hub/agents',
        name: 'AgentProfiles',
        component: () => import('@/views/contact-center/AgentProfilesView.vue'),
        meta: { requiredFeatures: ['engagement_hub.queues'] },
      },
      {
        path: 'engagement-hub/canned-responses',
        name: 'CannedResponses',
        component: () => import('@/views/contact-center/CannedResponsesView.vue'),
        meta: { requiredFeatures: ['engagement_hub.queues'] },
      },
      {
        path: 'engagement-hub/audio-library',
        name: 'AudioLibrary',
        component: () => import('@/views/contact-center/AudioLibraryView.vue'),
        meta: { requiredFeatures: ['engagement_hub.queues'] },
      },
      {
        path: 'engagement-hub/business-hours',
        name: 'BusinessHours',
        component: () => import('@/views/contact-center/BusinessHoursView.vue'),
        meta: { requiredFeatures: ['engagement_hub.queues'] },
      },
      {
        path: 'engagement-hub/queues',
        name: 'CCQueuesConfig',
        component: () => import('@/views/contact-center/QueuesConfigView.vue'),
        meta: { requiredFeatures: ['engagement_hub.queues'] },
      },
      {
        path: 'engagement-hub/hold-music',
        name: 'CCHoldMusic',
        component: () => import('@/views/contact-center/HoldMusicView.vue'),
        meta: { requiredFeatures: ['voice.telephony'] },
      },
      {
        path: 'engagement-hub/did-routing',
        name: 'CCDIDRouting',
        component: () => import('@/views/contact-center/DIDRoutingView.vue'),
        meta: { requiredFeatures: ['voice.telephony'] },
      },
      {
        path: 'engagement-hub/wallboard',
        name: 'CCWallboard',
        component: () => import('@/views/contact-center/WallboardView.vue'),
        meta: { requiredFeatures: ['engagement_hub.wallboard'] },
      },
      {
        path: 'engagement-hub/recordings',
        name: 'CCRecordings',
        component: () => import('@/views/contact-center/RecordingsView.vue'),
        meta: { requiredFeatures: ['voice.recordings'] },
      },
      // Campaigns
      {
        path: 'campaigns',
        name: 'CampaignsList',
        component: () => import('@/views/campaigns/CampaignsListView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      {
        path: 'campaigns/create',
        name: 'CampaignCreate',
        component: () => import('@/views/campaigns/CampaignBuilderView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      {
        path: 'campaigns/autopilot/create',
        name: 'AutopilotCreate',
        component: () => import('@/views/campaigns/AutopilotCreateView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      // v1.7.5 — Fleet live monitor. Must be registered BEFORE `campaigns/:id`
      // so the static path wins over the param route.
      {
        path: 'campaigns/live',
        name: 'CampaignsLiveFleet',
        component: () => import('@/views/campaigns/CampaignLiveFleetView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      {
        path: 'campaigns/:id',
        name: 'CampaignDetail',
        component: () => import('@/views/campaigns/CampaignDetailView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      {
        path: 'campaigns/:id/edit',
        name: 'CampaignEdit',
        component: () => import('@/views/campaigns/CampaignBuilderView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      {
        path: 'campaigns/:id/live',
        name: 'CampaignLive',
        component: () => import('@/views/campaigns/CampaignLiveView.vue'),
        meta: { requiredFeatures: ['outreach.broadcast_campaigns'] },
      },
      // WhatsApp Templates
      {
        path: 'whatsapp-templates',
        name: 'WATemplatesList',
        component: () => import('@/views/whatsapp-templates/WATemplatesListView.vue'),
        meta: { requiredFeatures: ['outreach.whatsapp_templates'] },
      },
      {
        path: 'whatsapp-templates/create',
        name: 'WATemplateCreate',
        component: () => import('@/views/whatsapp-templates/WATemplateEditorView.vue'),
        meta: { requiredFeatures: ['outreach.whatsapp_templates'] },
      },
      {
        path: 'whatsapp-templates/:id/edit',
        name: 'WATemplateEdit',
        component: () => import('@/views/whatsapp-templates/WATemplateEditorView.vue'),
        meta: { requiredFeatures: ['outreach.whatsapp_templates'] },
      },
      // Consent
      {
        path: 'consent',
        name: 'ConsentDashboard',
        component: () => import('@/views/consent/ConsentDashboardView.vue'),
        meta: { requiredFeatures: ['outreach.consent_management'] },
      },
      // Surveys (v1.6.1)
      {
        path: 'surveys',
        name: 'SurveysList',
        component: () => import('@/views/surveys/SurveysListView.vue'),
      },
      {
        path: 'surveys/new',
        name: 'SurveyNew',
        component: () => import('@/views/surveys/SurveyEditorView.vue'),
      },
      {
        path: 'surveys/:id',
        name: 'SurveyEdit',
        component: () => import('@/views/surveys/SurveyEditorView.vue'),
      },
      {
        path: 'surveys/:id/analytics',
        name: 'SurveyAnalytics',
        component: () => import('@/views/surveys/SurveyAnalyticsView.vue'),
      },
      // Settings
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/settings/SettingsView.vue'),
      },
      {
        path: 'settings/api-keys',
        name: 'ApiKeys',
        component: () => import('@/views/settings/ApiKeysView.vue'),
      },
      {
        path: 'settings/voice',
        name: 'VoiceSettings',
        component: () => import('@/views/settings/VoiceSettingsView.vue'),
      },
      {
        path: 'settings/tool-environment',
        name: 'ToolEnvironment',
        component: () => import('@/views/settings/ToolEnvironmentView.vue'),
      },
      {
        path: 'settings/messaging-policy',
        name: 'MessagingPolicy',
        component: () => import('@/views/settings/MessagingPolicyView.vue'),
        meta: { requiredFeatures: ['outreach.messaging_policy'] },
      },
      {
        path: 'settings/compliance',
        name: 'ComplianceSettings',
        component: () => import('@/views/settings/ComplianceSettingsView.vue'),
        meta: { requiredFeatures: ['security.compliance'] },
      },
      {
        path: 'settings/content-safety',
        name: 'ContentSafety',
        component: () => import('@/views/settings/ContentSafetyView.vue'),
        meta: { requiredFeatures: ['security.content_safety'] },
      },
      // CRM Sync (Generic CRM Layer — works with Slate, Salesforce, HubSpot, Banner, etc.)
      {
        path: 'integrations/crm-sync',
        name: 'CRMSync',
        component: () => import('@/views/settings/CRMIntegrationView.vue'),
        meta: { requiredFeatures: ['crm_data.crm_sync'] },
      },
      // Lifecycle Stages (contact funnel configuration)
      {
        path: 'contacts/lifecycle-stages',
        name: 'LifecycleStages',
        component: () => import('@/views/settings/LifecycleStagesView.vue'),
        meta: { requiredFeatures: ['crm_data.lifecycle_stages'] },
      },
      // Programs
      {
        path: 'programs',
        name: 'ProgramsList',
        component: () => import('@/views/programs/ProgramsView.vue'),
        meta: { requiredFeatures: ['outreach.programs'] },
      },
      // Sentiment Analytics
      {
        path: 'analytics/sentiment',
        name: 'SentimentAnalytics',
        component: () => import('@/views/reports/SentimentAnalyticsView.vue'),
        meta: { requiredFeatures: ['analytics.sentiment_analysis'] },
      },
      // Trend Analytics
      {
        path: 'analytics/trends',
        name: 'TrendAnalytics',
        component: () => import('@/views/reports/TrendAnalyticsView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      // Yield Predictions
      {
        path: 'enrollment/yield',
        name: 'YieldDashboard',
        component: () => import('@/views/enrollment/YieldDashboardView.vue'),
        meta: { requiredFeatures: ['outreach.yield_forecasts'] },
      },
      // Enrollment Forecasts
      {
        path: 'enrollment/forecasts',
        name: 'EnrollmentForecasts',
        component: () => import('@/views/enrollment/ForecastView.vue'),
        meta: { requiredFeatures: ['outreach.yield_forecasts'] },
      },
      // Slate CRM Integration
      {
        path: 'integrations/slate',
        name: 'SlateIntegration',
        component: () => import('@/views/integrations/SlateIntegrationView.vue'),
        meta: { requiredFeatures: ['crm_data.slate_crm'] },
      },
      // Banner SIS Integration
      {
        path: 'integrations/banner',
        name: 'BannerIntegration',
        component: () => import('@/views/integrations/BannerIntegrationView.vue'),
        meta: { requiredFeatures: ['crm_data.banner_sis'] },
      },
      // ROI & Conversion Tracking
      {
        path: 'reports/roi',
        name: 'ROIDashboard',
        component: () => import('@/views/reports/ROIDashboardView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      // Website Visitor Analytics
      {
        path: 'analytics/visitors',
        name: 'VisitorAnalytics',
        component: () => import('@/views/analytics/VisitorAnalyticsView.vue'),
        meta: { requiredFeatures: ['analytics.dashboard'] },
      },
      // Ambassador Program
      {
        path: 'ambassadors',
        name: 'Ambassadors',
        component: () => import('@/views/ambassador/AmbassadorView.vue'),
        meta: { requiredFeatures: ['outreach.ambassadors'] },
      },
      // Call Tracking
      {
        path: 'call-tracking',
        name: 'CallTracking',
        component: () => import('@/views/call-tracking/CallTrackingView.vue'),
        meta: { requiredFeatures: ['voice.call_tracking'] },
      },
      // Intelligence Engine Settings (OmniCortex)
      {
        path: 'intelligence/engines',
        name: 'IntelligenceEngines',
        component: () => import('@/views/intelligence/EngineSettingsView.vue'),
        meta: { requiredFeatures: ['knowledge.intelligence_engines'] },
      },
      // Coaching Playbooks
      {
        path: 'intelligence/playbooks',
        name: 'PlaybooksList',
        component: () => import('@/views/intelligence/PlaybooksListView.vue'),
      },
      {
        path: 'intelligence/playbooks/new',
        name: 'PlaybookCreate',
        component: () => import('@/views/intelligence/PlaybookEditorView.vue'),
      },
      {
        path: 'intelligence/playbooks/:id/edit',
        name: 'PlaybookEditor',
        component: () => import('@/views/intelligence/PlaybookEditorView.vue'),
      },
      {
        path: 'intelligence/crystallization',
        name: 'CrystallizationReview',
        component: () => import('@/views/intelligence/CrystallizationReviewView.vue'),
      },
      // Workforce Management (OmniCortex Phase 7)
      {
        path: 'workforce/schedules',
        name: 'ScheduleConfig',
        component: () => import('@/views/workforce/ScheduleConfigView.vue'),
        meta: { requiredFeatures: ['engagement_hub.schedules'] },
      },
      // Compliance Heatmap (OmniCortex Phase 7)
      {
        path: 'compliance/heatmap',
        name: 'ComplianceHeatmap',
        component: () => import('@/views/compliance/ComplianceRulesView.vue'),
        meta: { requiredFeatures: ['analytics.audit_logs'] },
      },
      // Script Evolution (OmniCortex Phase 8)
      {
        path: 'scripts/evolution',
        name: 'ScriptEvolution',
        component: () => import('@/views/scripts/ScriptEvolutionView.vue'),
        meta: { requiredFeatures: ['engagement_hub.script_evolution'] },
      },
      // Root Cause Analysis (OmniCortex Phase 8)
      {
        path: 'analytics/root-causes',
        name: 'RootCauseAnalysis',
        component: () => import('@/views/analytics/RootCauseView.vue'),
        meta: { requiredFeatures: ['engagement_hub.root_causes'] },
      },
      // Emotional Resolution (OmniCortex Phase 8)
      {
        path: 'analytics/emotional-resolution',
        name: 'EmotionalResolution',
        component: () => import('@/views/analytics/EmotionalResolutionView.vue'),
        meta: { requiredFeatures: ['engagement_hub.emotional_resolution'] },
      },
      // Vertex AI Search
      {
        path: 'vertex-ai-search/datastores',
        name: 'DataStoresList',
        component: () => import('@/views/vertex-ai-search/DataStoresView.vue'),
        meta: { requiredFeatures: ['knowledge.vertex_ai_search'] },
      },
      {
        path: 'vertex-ai-search/datastores/:dsId',
        name: 'DataStoreDetail',
        component: () => import('@/views/vertex-ai-search/DataStoreDetailView.vue'),
        meta: { requiredFeatures: ['knowledge.vertex_ai_search'] },
      },
      {
        path: 'vertex-ai-search/engines',
        name: 'EnginesList',
        component: () => import('@/views/vertex-ai-search/EnginesView.vue'),
        meta: { requiredFeatures: ['knowledge.vertex_ai_search'] },
      },
      {
        path: 'vertex-ai-search/test',
        name: 'TestSearch',
        component: () => import('@/views/vertex-ai-search/TestSearchView.vue'),
        meta: { requiredFeatures: ['knowledge.vertex_ai_search'] },
      },
    ],
  },
  // Catch all - redirect to dashboard or login
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if (to.meta.requiresAuth === false && authStore.isAuthenticated && to.name !== 'SSOCallback') {
    // Redirect authenticated users away from login/forgot-password pages
    next({ name: 'Dashboard' })
  } else {
    next()
  }
})

// Subscription feature gate guard
router.beforeEach(async (to, from, next) => {
  const requiredFeatures = to.meta.requiredFeatures as string[] | undefined;
  if (!requiredFeatures?.length) return next();

  // Full access users bypass
  const authStore = useAuthStore();
  if (authStore.hasPermission('portal.full_access') ||
      authStore.user?.userType === 'super_admin' ||
      authStore.user?.userType === 'partner_admin' ||
      authStore.user?.userType === 'tenant_admin') {
    return next();
  }

  const { hasAnyFeature, loaded, load } = useEntitlements();
  if (!loaded.value) await load();

  if (hasAnyFeature(requiredFeatures)) return next();

  // Redirect to dashboard with feature param
  next({ path: '/', query: { locked: requiredFeatures[0] } });
})

export default router
